import React from 'react';
import { Body } from '../../../Constants/Fonts';

// RiskHeatmap — a 5×5 impact × likelihood grid rendered in the shared house
// style (matches src/Dashboard/RiskRegistry/RiskGraph.js): Impact on the Y axis
// (Catastrophic→Insignificant, top→bottom), Likelihood on the X axis
// (Rare→Almost Certain), the vibrant green→red palette, a Low→High gradient bar
// on top, and every count shown. `matrix[likelihoodIdx][impactIdx]` holds the
// count for that cell (both 0-based, so index 0 = value 1).
//
// Props:
//   matrix      — 5×5 counts (from risk_portal_heatmap)
//   showScores  — render the numeric score (and "N risks") in each cell (full
//                 Heat Map page). Default false = compact count-only (dashboard).
//   activeCell  — "L,I" key of the currently drilled-into cell (highlighted)
//   onCellClick — (likelihood, impact) => void

// Impact rows, top (5) → bottom (1).
const IMPACT_ROWS = [
    { val: 5, label: 'Catastrophic' },
    { val: 4, label: 'Major' },
    { val: 3, label: 'Moderate' },
    { val: 2, label: 'Minor' },
    { val: 1, label: 'Insignificant' },
];

// Likelihood columns, left (1) → right (5).
const LIKELIHOOD_COLS = [
    { val: 1, label: 'Rare' },
    { val: 2, label: 'Unlikely' },
    { val: 3, label: 'Possible' },
    { val: 4, label: 'Likely' },
    { val: 5, label: 'Almost Certain' },
];

// Shared house palette (keyed by impact value → likelihood-1 index) — identical
// to the dashboard Risk Heatmap so the two read the same everywhere.
const COLOR_MATRIX = {
    5: ['#D9BF21', '#DE9E30', '#DE9E30', '#D24646', '#991b1b'],
    4: ['#D9BF21', '#D9BF21', '#DE9E30', '#D24646', '#D24646'],
    3: ['#19A07E', '#D9BF21', '#D9BF21', '#DE9E30', '#DE9E30'],
    2: ['#19A07E', '#19A07E', '#D9BF21', '#D9BF21', '#DE9E30'],
    1: ['#19A07E', '#19A07E', '#19A07E', '#D9BF21', '#D9BF21'],
};

const cellColor = (impactVal, likVal) => COLOR_MATRIX[impactVal]?.[likVal - 1] || '#19A07E';

export default function RiskHeatmap({ matrix, showScores = false, activeCell = null, onCellClick }) {
    const countAt = (likVal, impactVal) => (matrix?.[likVal - 1]?.[impactVal - 1]) || 0;

    return (
        <div className={`rp1_heatmap${showScores ? ' rp1_heatmap--lg' : ''}`}>
            <div className="rp1_heatmap_main">
                <div className="rp1_heatmap_ytitle">IMPACT</div>

                <div className="rp1_heatmap_body">
                    {/* Low → High gradient bar, aligned to the cells. */}
                    <div className="rp1_heatmap_gradient">
                        <div className="rp1_heatmap_gutter" />
                        <div className="rp1_heatmap_gradient_col">
                            <div className="rp1_heatmap_gradient_info">
                                <span>Low</span>
                                <span>High</span>
                            </div>
                            <div className="rp1_heatmap_gradient_bar" />
                        </div>
                    </div>

                    {/* Rows: impact 5 → 1 */}
                    {IMPACT_ROWS.map((row) => (
                        <div key={row.val} className="rp1_heatmap_row">
                            <Body as="span" variant={2} color="var(--color-text-secondary)" className="rp1_heatmap_ylabel">
                                {row.label}
                            </Body>
                            <div className="rp1_heatmap_cells">
                                {LIKELIHOOD_COLS.map((col) => {
                                    const count = countAt(col.val, row.val);
                                    const score = row.val * col.val;
                                    const isActive = activeCell === `${col.val},${row.val}`;
                                    return (
                                        <div
                                            key={col.val}
                                            className={`rp1_heatmap_cell${isActive ? ' rp1_heatmap_cell--active' : ''}`}
                                            onClick={onCellClick ? () => onCellClick(col.val, row.val) : undefined}
                                            style={{
                                                background: cellColor(row.val, col.val),
                                                cursor: onCellClick ? 'pointer' : 'default',
                                            }}
                                        >
                                            {showScores ? (
                                                <>
                                                    <span className="rp1_heatmap_score">{score}</span>
                                                    {count > 0 && (
                                                        <span className="rp1_heatmap_sub">{count} risk{count > 1 ? 's' : ''}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="rp1_heatmap_count">{count}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {/* Likelihood labels under the grid, aligned past the y gutter. */}
                    <div className="rp1_heatmap_xaxis">
                        <div className="rp1_heatmap_gutter" />
                        <div className="rp1_heatmap_xlabels">
                            {LIKELIHOOD_COLS.map((col) => (
                                <Body key={col.val} as="span" variant={2} color="var(--color-text-secondary)" className="rp1_heatmap_xlabel">
                                    {col.label}
                                </Body>
                            ))}
                        </div>
                    </div>

                    <div className="rp1_heatmap_xtitle">LIKELIHOOD</div>
                </div>
            </div>
        </div>
    );
}
